import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl } from '@angular/forms';
import { AuthservicesService } from '../../../services/authservices.service';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-mentor-manager',
  templateUrl: './mentor-manager.component.html',
  styleUrls: ['./mentor-manager.component.css']
})
export class MentorManagerComponent implements OnInit {
  loading = false;
  mentorArr = [];
  arr = [1, 2, 3, 4, 5];
  
  // Pagination for mentor list
  mentorSkip = 0;
  mentorLimit = 10;
  mentorTotal = 0;
  mentorSearchTerm = '';
  mentorSearchTimeout: any;
  
  // View mentor details
  viewingMentor: any = null;
  
  // Form data
  mentorForm: FormGroup;
  submitted = false;
  editingMentor: any = null;
  
  // Options
  mentorshipCategories = [
    'Financial Management',
    'Relationships & Marriage',
    'Parenting',
    'Leadership / Executive',
    'Entrepreneurship',
    'Career/Professional Development',
    'Health & Wellness',
    'Teaching / Coaching',
    'Spiritual Growth',
    'Crisis Management',
    'Emotional Management',
    'Becoming'
  ];
  
  mentorshipTypes = [
    'One-on-One',
    'Peer',
    'Group',
    'Reverse',
    'Flash/One-Off',
    'Team',
    'Virtual'
  ];
  
  servingOptions = ['Men', 'Women'];
  
  // User selection with pagination
  candidateUsers: any[] = [];
  filteredUsers: any[] = [];
  userSearchTerm: string = '';
  selectedUser: any = null;
  showError = false;
  currentUser: any;
  ministryData: any;
  
  // Pagination for user selection
  userSkip = 0;
  userLimit = 10;
  userTotal = 0;
  loadingUsers = false;
  hasMoreUsers = true;
  userSearchTimeout: any;

  constructor(
    private authService: AuthservicesService,
    private router: Router,
    private fb: FormBuilder,
    private modalService: NgbModal
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem('ministry_currentUser') || '{}');
    this.loadMentors();
    this.loadMinistryData();
  }

  initForm() {
    this.mentorForm = this.fb.group({
      profileLink: ['', [this.urlValidator]],
      mentorshipDetails: ['', [Validators.required, Validators.maxLength(500)]],
      serving: this.fb.array([]),
      mentorshipCategory: this.fb.array([]),
      mentorshipType: this.fb.array([]),
      selectedUserId: ['', Validators.required]
    });
    
    // Initialize checkboxes
    this.servingOptions.forEach(() => this.servingFormArray.push(new FormControl(false)));
    this.mentorshipCategories.forEach(() => this.categoryFormArray.push(new FormControl(false)));
    this.mentorshipTypes.forEach(() => this.typeFormArray.push(new FormControl(false)));
  }

  get servingFormArray() {
    return this.mentorForm.get('serving') as FormArray;
  }

  get categoryFormArray() {
    return this.mentorForm.get('mentorshipCategory') as FormArray;
  }

  get typeFormArray() {
    return this.mentorForm.get('mentorshipType') as FormArray;
  }

  get f() {
    return this.mentorForm ? this.mentorForm.controls : {};
  }

  loadMentors(reset: boolean = true) {
    if (reset) {
      this.mentorSkip = 0;
      this.mentorArr = [];
    }
    
    if (this.loading) {
      return; // Prevent multiple simultaneous requests
    }
    
    const communityId = this.currentUser?.id || this.currentUser?._id;
    
    if (!communityId) {
      this.loading = false;
      this.authService.successMessage('Community ID not found', 'error');
      return;
    }
    
    this.loading = true;
    
    // Call API with communityId, pagination and search parameters
    this.authService.getMentorsByCommunity(
      communityId,
      this.mentorSkip,
      this.mentorLimit,
      this.mentorSearchTerm
    ).subscribe(
      (res: any) => {
        const mentors = res?.mentors || res?.items || [];
        const total = res?.total || res?.count || 0;
        
        if (reset) {
          this.mentorArr = mentors;
        } else {
          // Append new mentors for infinite scroll
          this.mentorArr = [...this.mentorArr, ...mentors];
        }
        
        this.mentorTotal = total;
        this.loading = false;
      },
      error => {
        console.error('Error loading mentors:', error);
        this.loading = false;
        this.authService.successMessage(
          error?.error?.message || 'Error loading mentors',
          'error'
        );
      }
    );
  }

  onMentorSearchChange(event: any) {
    const searchTerm = (event?.target?.value || '').trim();
    
    if (this.mentorSearchTimeout) {
      clearTimeout(this.mentorSearchTimeout);
    }
    
    this.mentorSearchTimeout = setTimeout(() => {
      this.mentorSearchTerm = searchTerm;
      this.mentorSkip = 0; // Reset pagination on search
      this.loadMentors(true); // Reload from API with search
    }, 500);
  }

  viewMentor(mentor: any, modalTemplate: any) {
    // Use mentor data from the list (already has all details from /api/community/mentors)
    this.viewingMentor = mentor;
    this.modalService.open(modalTemplate, { scrollable: true, backdrop: 'static', keyboard: false, size: 'lg' });
    
    // If user data is not populated, try to load it
    if (mentor._id && !mentor.user && mentor.userId) {
      // Try to find user in candidateUsers list
      const user = this.candidateUsers.find(u => u._id === mentor.userId);
      if (user) {
        this.viewingMentor.user = user;
      } else {
        // Fallback: load mentor details if user data is missing
        this.authService.getMentorById(mentor._id).subscribe(
          (res: any) => {
            this.viewingMentor = res;
          },
          error => {
            console.error('Error loading mentor details:', error);
            // Continue with existing data even if API call fails
          }
        );
      }
    }
  }

  nextMentorPage() {
    if ((this.mentorSkip + this.mentorLimit) < this.mentorTotal && !this.loading) {
      this.mentorSkip += this.mentorLimit;
      this.loadMentors(false); // Load next page
    }
  }

  prevMentorPage() {
    if (this.mentorSkip > 0) {
      this.mentorSkip = Math.max(0, this.mentorSkip - this.mentorLimit);
      this.loadMentors(true); // Reload from beginning
    }
  }

  onMentorTableScroll(event: any) {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    // Load more if scrolled near bottom and there are more mentors
    if (atBottom && 
        (this.mentorSkip + this.mentorLimit) < this.mentorTotal && 
        !this.loading) {
      this.nextMentorPage();
    }
  }

  getCurrentPage(): number {
    return Math.floor(this.mentorSkip / this.mentorLimit) + 1;
  }

  getTotalPages(): number {
    return Math.ceil(this.mentorTotal / this.mentorLimit);
  }

  loadMinistryData() {
    const currentId = this.currentUser?.id || this.currentUser?._id;
    if (currentId) {
      this.authService.communitydetails(currentId).subscribe((res: any) => {
        if (res?.code == 200) {
          this.ministryData = res?.data;
        }
      });
    }
  }

  getUserForMentor(reset: boolean = false) {
    if (reset) {
      this.userSkip = 0;
      this.candidateUsers = [];
      this.filteredUsers = [];
      this.hasMoreUsers = true;
    }

    if (!this.hasMoreUsers || this.loadingUsers) {
      return;
    }

    this.loadingUsers = true;
    this.authService.getUsersForMentor(this.userSkip, this.userLimit, this.userSearchTerm).subscribe(
      (res: any) => {
        // Handle the response structure: { success, total, skip, limit, users: [...] }
        const items = Array.isArray(res?.users) ? res.users : 
                     (Array.isArray(res?.items) ? res.items : 
                     (Array.isArray(res?.data) ? res.data : []));
        const total = res?.total || res?.count || items.length;
        
        // Process users to ensure consistent structure
        const processedItems = items.map((user: any) => {
          // Construct name if not present
          if (!user.name && (user.firstName || user.lastName)) {
            user.name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
          
          // Handle photo - convert string "null" or "undefined" to null
          if (user.photo === 'null' || user.photo === 'undefined' || user.photo === null || user.photo === undefined) {
            user.photo = null;
          }
          
          return user;
        });
        
        if (reset) {
          this.candidateUsers = processedItems;
          this.filteredUsers = processedItems;
        } else {
          this.candidateUsers = [...this.candidateUsers, ...processedItems];
          this.filteredUsers = [...this.filteredUsers, ...processedItems];
        }
        
        this.userTotal = total;
        this.userSkip += processedItems.length;
        this.hasMoreUsers = this.candidateUsers.length < total;
        this.loadingUsers = false;
      },
      error => {
        console.error('Error loading users:', error);
        this.loadingUsers = false;
        this.authService.successMessage(
          error?.error?.message || 'Error loading users',
          'error'
        );
      }
    );
  }

  getMentorshipDetailsLength(): number {
    return (this.mentorForm.get('mentorshipDetails')?.value || '').length;
  }

  addMentor(modalTemplate?: any) {
    this.resetForm();
    if (modalTemplate) {
      this.modalService.open(modalTemplate, { scrollable: true, backdrop: 'static', keyboard: false, size: 'lg' });
    }
  }

  editMentor(mentor: any, modalTemplate: any) {
    this.editingMentor = mentor;
    this.showError = false;
    this.submitted = false;
    
    // Load full mentor details if needed
    if (mentor._id && !mentor.user) {
      this.authService.getMentorById(mentor._id).subscribe(
        (res: any) => {
          this.populateFormWithMentorData(res);
          this.modalService.open(modalTemplate, { scrollable: true, backdrop: 'static', keyboard: false, size: 'lg' });
        },
        error => {
          console.error('Error loading mentor details:', error);
          this.authService.successMessage('Error loading mentor details', 'error');
        }
      );
    } else {
      this.populateFormWithMentorData(mentor);
      this.modalService.open(modalTemplate, { scrollable: true, backdrop: 'static', keyboard: false, size: 'lg' });
    }
  }

  populateFormWithMentorData(mentor: any) {
    // Populate form with mentor data
    this.mentorForm.patchValue({
      profileLink: mentor.profileLink || '',
      mentorshipDetails: mentor.mentorshipDetails || '',
      selectedUserId: mentor.user?._id || mentor.userId || ''
    });
    
    // Set serving checkboxes
    if (mentor.serving && Array.isArray(mentor.serving)) {
      this.servingOptions.forEach((option, index) => {
        this.servingFormArray.at(index).setValue(mentor.serving.includes(option));
      });
    }
    
    // Set category checkboxes
    if (mentor.mentorshipCategory && Array.isArray(mentor.mentorshipCategory)) {
      this.mentorshipCategories.forEach((category, index) => {
        this.categoryFormArray.at(index).setValue(mentor.mentorshipCategory.includes(category));
      });
    }
    
    // Set type checkboxes
    if (mentor.mentorshipType && Array.isArray(mentor.mentorshipType)) {
      this.mentorshipTypes.forEach((type, index) => {
        this.typeFormArray.at(index).setValue(mentor.mentorshipType.includes(type));
      });
    }
    
    // Set selected user
    if (mentor.user) {
      this.selectedUser = mentor.user;
    } else if (mentor.userId) {
      // If user is not in the list, we'll need to fetch it or use the ID
      this.selectedUser = { _id: mentor.userId };
    }
  }

  openUserSelector(modalTemplate: any) {
    this.userSearchTerm = '';
    this.userSkip = 0;
    this.candidateUsers = [];
    this.filteredUsers = [];
    this.hasMoreUsers = true;
    this.getUserForMentor(true);
    this.modalService.open(modalTemplate, { scrollable: true, backdrop: 'static', keyboard: false });
  }

  onUserSearchChange(event: any) {
    const q = (event?.target?.value || '').trim();
    
    // Clear previous timeout
    if (this.userSearchTimeout) {
      clearTimeout(this.userSearchTimeout);
    }
    
    // Debounce search - wait 500ms after user stops typing
    this.userSearchTimeout = setTimeout(() => {
      this.userSearchTerm = q;
      this.userSkip = 0;
      this.candidateUsers = [];
      this.filteredUsers = [];
      this.hasMoreUsers = true;
      this.getUserForMentor(true);
    }, 500);
  }

  loadMoreUsers() {
    if (!this.loadingUsers && this.hasMoreUsers) {
      this.getUserForMentor(false);
    }
  }

  onUserListScroll(event: any) {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
    
    if (atBottom && this.hasMoreUsers && !this.loadingUsers) {
      this.loadMoreUsers();
    }
  }

  selectUser(user: any, modal: any) {
    this.selectedUser = user;
    this.mentorForm.patchValue({ selectedUserId: user._id });
    modal.close('selected');
  }

  getSelectedServing(): string[] {
    return this.servingOptions.filter((option, index) => this.servingFormArray.at(index).value);
  }

  getSelectedCategories(): string[] {
    return this.mentorshipCategories.filter((category, index) => this.categoryFormArray.at(index).value);
  }

  getSelectedTypes(): string[] {
    return this.mentorshipTypes.filter((type, index) => this.typeFormArray.at(index).value);
  }

  submitMentor(modal: any) {
    this.submitted = true;
    this.showError = false;

    // Validate form
    if (this.mentorForm.invalid) {
      this.showError = true;
      this.authService.scrollToError();
      return;
    }

    const formValue = this.mentorForm.value;
    const selectedServing = this.getSelectedServing();
    const selectedCategories = this.getSelectedCategories();
    const selectedTypes = this.getSelectedTypes();

    // Validate required fields
    if (selectedServing.length === 0) {
      this.showError = true;
      this.authService.successMessage('Please select at least one serving option', 'error');
      return;
    }

    if (selectedCategories.length === 0) {
      this.showError = true;
      this.authService.successMessage('Please select at least one mentorship category', 'error');
      return;
    }

    if (selectedTypes.length === 0) {
      this.showError = true;
      this.authService.successMessage('Please select at least one mentorship type', 'error');
      return;
    }

    if (!formValue.selectedUserId) {
      this.showError = true;
      this.authService.successMessage('Please select a mentor profile', 'error');
      return;
    }

    if (!formValue.mentorshipDetails || formValue.mentorshipDetails.trim().length === 0) {
      this.showError = true;
      this.authService.successMessage('Mentorship details is required', 'error');
      return;
    }

    if (formValue.mentorshipDetails.length > 500) {
      this.showError = true;
      this.authService.successMessage('Mentorship details cannot exceed 500 characters', 'error');
      return;
    }

    const communityId = this.currentUser?.id || this.currentUser?._id;
    if (!communityId) {
      this.showError = true;
      this.authService.successMessage('Community ID not found', 'error');
      return;
    }

    // Prepare mentor data according to backend model
    const mentorData = {
      user: formValue.selectedUserId,
      profileLink: formValue.profileLink && formValue.profileLink.trim() ? formValue.profileLink.trim() : null,
      mentorshipDetails: formValue.mentorshipDetails.trim(),
      serving: selectedServing,
      mentorshipCategory: selectedCategories,
      mentorshipType: selectedTypes
    };

    if (this.editingMentor) {
      // Update mentor
      this.authService.updateMentor(this.editingMentor._id, mentorData).subscribe(
        (res: any) => {
          this.authService.successMessage(res.message || 'Mentor updated successfully', 'success').then(() => {
            this.loadMentors(true);
            this.resetForm();
            modal.close('saved');
          });
        },
        error => {
          console.error('Error updating mentor:', error);
          this.authService.successMessage(
            error?.error?.message || 'Error updating mentor',
            'error'
          );
        }
      );
    } else {
      // Add mentor
      this.authService.addMentor(communityId, mentorData).subscribe(
        (res: any) => {
          this.authService.successMessage(res.message || 'Mentor added successfully', 'success').then(() => {
            this.loadMentors(true);
            this.resetForm();
            modal.close('saved');
          });
        },
        error => {
          console.error('Error adding mentor:', error);
          const errorMessage = error?.error?.message || 'Error adding mentor';
          
          // Handle duplicate mentor error
          if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
            this.authService.successMessage('This mentor already exists for this ministry', 'error');
          } else {
            this.authService.successMessage(errorMessage, 'error');
          }
        }
      );
    }
  }

  resetForm() {
    this.mentorForm.reset();
    this.initForm();
    this.selectedUser = null;
    this.submitted = false;
    this.showError = false;
    this.editingMentor = null;
  }

  deleteMentor(mentor: any) {
    const swal = this.authService.swal('delete');
    swal.then((result) => {
      if (result.value == true) {
        this.authService.deleteMentor(mentor._id).subscribe(
          (res: any) => {
            this.authService.successMessage(
              res.message || 'Mentor deleted successfully',
              'success'
            ).then(() => {
              this.loadMentors(true);
            });
          },
          error => {
            console.error('Error deleting mentor:', error);
            this.authService.successMessage(
              error?.error?.message || 'Error deleting mentor',
              'error'
            );
          }
        );
      }
    });
  }

  goBack() {
    this.router.navigate(['/alerts']);
  }

  getMinistryContact() {
    if (this.ministryData) {
      return {
        email: this.ministryData.publicEmail || this.ministryData.email,
        phone: this.ministryData.contactNo,
        address: this.ministryData.address?.googleAddress || this.ministryData.address?.streetAddress
      };
    }
    return null;
  }

  renderImage(photo: string | null) {
    if (!photo || photo === 'null' || photo === 'undefined') {
      return 'assets/images/church.jpeg';
    }
    
    if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
    }
    
    // Handle relative paths
    if (photo.includes('uploads')) {
      const parts = photo.split('uploads');
      if (parts.length > 1) {
        return `http://localhost:9000/uploads${parts[1]}`;
      }
    }
    
    // If it's already a full path starting with uploads
    if (photo.startsWith('uploads/')) {
      return `http://localhost:9000/${photo}`;
    }
    
    return 'assets/images/church.jpeg';
  }

  getErrorImage(event: any) {
    this.authService.geterrorImage(event);
  }

  getUserDisplayName(user: any): string {
    if (user.name) {
      return user.name;
    }
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (user.userName) {
      return user.userName;
    }
    if (user.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  }

  urlValidator(control: FormControl) {
    if (!control.value || control.value.trim() === '') {
      return null; // Optional field, so empty is valid
    }
    
    const url = control.value.trim();
    
    // More comprehensive URL validation that accepts:
    // - http:// and https:// URLs
    // - URLs with hash fragments (#)
    // - URLs with query parameters (?)
    // - URLs with ports
    // - Localhost URLs
    try {
      // Use the URL constructor for validation (most reliable)
      new URL(url);
      return null; // Valid URL
    } catch (e) {
      // If URL constructor fails, try regex pattern
      // Pattern accepts: http://, https://, or protocol-relative URLs
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.\-?#=&%]*)*\/?$/i;
      if (urlPattern.test(url)) {
        return null;
      }
      return { invalidUrl: true };
    }
  }
}
